import React, { useState, useEffect } from 'react';
import { Search, MapPin, Filter, Briefcase, Clock, Users } from 'lucide-react';
import { getJobOpportunities } from '../../services/jobService';
import JobCard from './components/JobCard';
import FilterPanel from './components/FilterPanel';
import JobDetailModal from './components/JobDetailModal';
import Icon from '../../components/AppIcon';


const JobOpportunities = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [filters, setFilters] = useState({
    category: null,
    jobType: null,
    experienceLevel: null,
    location: null,
    isRemote: null,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    totalCount: 0,
    hasMore: false,
  });

  // Fetch jobs with current filters
  const fetchJobs = async (resetPage = false) => {
    try {
      setLoading(true);
      const page = resetPage ? 1 : pagination?.page;
      
      const result = await getJobOpportunities({
        page,
        limit: pagination?.limit,
        searchQuery: searchQuery || null,
        ...filters,
      });

      setJobs(resetPage ? result?.jobs || [] : [...jobs, ...(result?.jobs || [])]);
      setPagination({
        page: result?.page || 1,
        limit: result?.limit || 12,
        totalCount: result?.totalCount || 0,
        hasMore: result?.hasMore || false,
      });
    } catch (err) {
      setError('Failed to load job opportunities');
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchJobs(true);
  }, [searchQuery, filters]);

  // Handle search
  const handleSearch = (e) => {
    e?.preventDefault();
    fetchJobs(true);
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Load more jobs
  const loadMore = () => {
    if (!loading && pagination?.hasMore) {
      setPagination(prev => ({ ...prev, page: prev?.page + 1 }));
      fetchJobs();
    }
  };

  // Statistics cards data
  const stats = [
    {
      title: 'Total Jobs',
      value: pagination?.totalCount || 0,
      icon: Briefcase,
      color: 'bg-blue-500',
    },
    {
      title: 'Active Openings',
      value: jobs?.filter(job => job?.status === 'active')?.length || 0,
      icon: Clock,
      color: 'bg-green-500',
    },
    {
      title: 'Remote Available',
      value: jobs?.filter(job => job?.is_remote)?.length || 0,
      icon: MapPin,
      color: 'bg-purple-500',
    },
    {
      title: 'New This Week',
      value: jobs?.filter(job => {
        const weekAgo = new Date();
        weekAgo?.setDate(weekAgo?.getDate() - 7);
        return new Date(job?.created_at) > weekAgo;
      })?.length || 0,
      icon: Users,
      color: 'bg-orange-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Job Opportunities</h1>
              <p className="text-gray-600 mt-1">
                Discover career opportunities in smart cities and urban innovation
              </p>
            </div>
            
            {/* Search Form */}
            <form onSubmit={handleSearch} className="flex gap-2 max-w-md w-full md:w-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats?.map((stat, index) => {
            const Icon = stat?.icon;
            return (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm border">
                <div className="flex items-center">
                  <div className={`${stat?.color} rounded-lg p-3 mr-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat?.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat?.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-8">
        <div className="flex gap-6">
          {/* Filters Sidebar */}
          {showFilters && (
            <div className="w-80 flex-shrink-0">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClose={() => setShowFilters(false)}
              />
            </div>
          )}

          {/* Job Listings */}
          <div className="flex-1">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            {loading && jobs?.length === 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)]?.map((_, index) => (
                  <div key={index} className="bg-white rounded-lg p-6 shadow-sm border animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3 mb-4"></div>
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-200 rounded"></div>
                      <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {jobs?.map((job) => (
                    <JobCard
                      key={job?.id}
                      job={job}
                      onViewDetails={() => setSelectedJob(job)}
                    />
                  ))}
                </div>

                {jobs?.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <Briefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
                    <p className="text-gray-600">
                      Try adjusting your search criteria or check back later for new opportunities.
                    </p>
                  </div>
                )}

                {pagination?.hasMore && (
                  <div className="text-center mt-8">
                    <button
                      onClick={loadMore}
                      disabled={loading}
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Loading...' : 'Load More Jobs'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          isOpen={!!selectedJob}
          onClose={() => setSelectedJob(null)}
        />
      )}
    </div>
  );
};

export default JobOpportunities;